/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

import classnames from 'classnames';

import { INosSubscriptionDetailsData } from './nos-subscription-details.data';
import { INosSubscriptionDetailsProps, INosSubscriptionDetailsResources } from './nos-subscription-details.props.autogenerated';
import { Module, Node, IModuleProps, INodeProps, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import getSubscriptionDetailsAction, {
    GetSubscriptionDetailsInput,
    IGetSubscriptionDetailsData
} from '../../actions/get-subscription-details.action';
import {
    formatCurrency,
    formatDate,
    getInvoiceFrequency,
    getInvoiceStatus,
    getSubscriptionStatus
} from './utils/nos-subscription-details.utils';
import cancelSubscriptionAction, { CancelSubscriptionInput } from '../../actions/cancel-subscription.action';
import Button from 'reactstrap/lib/Button';
import changeCustomerReferenceAction, { ChangeCustomerReferenceInput } from '../../actions/change-customer-reference.action';

export interface INosSubscriptionDetailsViewProps extends INosSubscriptionDetailsProps<INosSubscriptionDetailsData> {
    content: INodeProps;
    heading: INodeProps;
    billingAddress: INodeProps;
    contactInformation: INodeProps;
    relatedInvoices: INodeProps;
    subscriptionTotals: INodeProps;
    className?: string;
}

export interface INosSubscriptionDetailsExtendedProps extends INosSubscriptionDetailsProps<INosSubscriptionDetailsData> {}

/**
 * Interface for INosSubscriptionDetailsState.
 */
export interface INosSubscriptionDetailsState {
    editCustomerReference: boolean;
    isCanceled: boolean;
    subscriptionDetailsData?: IGetSubscriptionDetailsData;
    isModalOpen: boolean;
    customerReferenceState: string;
}

/**
 *
 * NosSubscriptionDetails component
 * @extends {React.PureComponent<INosSubscriptionDetailsProps<INosSubscriptionDetailsData>>}
 */
class NosSubscriptionDetails extends React.PureComponent<
    INosSubscriptionDetailsProps<INosSubscriptionDetailsData>,
    INosSubscriptionDetailsState
> {
    private readonly customerReferenceRef: React.RefObject<HTMLInputElement>;

    public constructor(props: INosSubscriptionDetailsExtendedProps) {
        super(props);

        this.state = {
            editCustomerReference: false,
            customerReferenceState: '',
            isCanceled: false,
            subscriptionDetailsData: undefined,
            isModalOpen: false
        };

        this.customerReferenceRef = React.createRef();
    }

    private _initialize = async () => {
        const actionInput = new GetSubscriptionDetailsInput();

        await getSubscriptionDetailsAction(actionInput, this.props.context.actionContext).then(res => {
            this.setState({ subscriptionDetailsData: res });
            this.setState({ customerReferenceState: res.CustomerReference });
            this.setState({ editCustomerReference: false });
        });
    };

    public componentDidMount(): void {
        if (this.props.context.actionContext.requestContext.user.isAuthenticated) {
            // Initializing data props
            void this._initialize();
        }
    }

    public shouldComponentUpdate(nextProps: INosSubscriptionDetailsExtendedProps, nextState: INosSubscriptionDetailsState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading.text = event.target.value);

    public render(): JSX.Element | null {
        const { resources, context } = this.props;

        const modalContainer = {
            moduleProps: this.props,
            tag: Modal,
            className: 'nos-subscription-details__dialog',
            isOpen: true,
            'aria-label': 'subscription-cancel',
            toggle: this.closeModal,
            modalTransition: { timeout: 0 },
            shouldUpdateFocusToFirstElement: true
        };

        const modalHeaderContainer = {
            tag: ModalHeader,
            className: 'nos-subscription-details_modal-header',
            toggle: this.closeModal,
            closeButtonTabIndex: 1
        };

        const modalFooterContainer = { tag: ModalFooter, className: 'nos-subscription-details__modal-footer' };

        const modalBodyContainer = { tag: ModalBody, className: 'nos-subscription-details__modal-body' };

        const viewProps = {
            ...this.props,
            heading: this.props.config.heading && (
                <Msdyn365.Text
                    className=''
                    tag={this.props.config.heading.tag || 'h1'}
                    text={this.props.config.heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            className: classnames('nos-subscription-details', this.props.config.className),
            content: this._renderContent(
                resources,
                context,
                modalContainer,
                modalHeaderContainer,
                modalFooterContainer,
                modalBodyContainer,
                this.state.subscriptionDetailsData
            ),
            billingAddress: this._renderBillingAddress(resources, this.state.subscriptionDetailsData),
            contactInformation: this._renderContactInformation(resources, this.state.subscriptionDetailsData),
            relatedInvoices: this._renderRelatedInvoices(resources, context, this.state.subscriptionDetailsData),
            subscriptionTotals: this._renderSubscriptionTotals(resources, this.state.subscriptionDetailsData)
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _renderContent = (
        resources: INosSubscriptionDetailsResources,
        context: any,
        modalContainer: IModuleProps,
        modalHeaderContainer: INodeProps,
        modalFooterContainer: INodeProps,
        modalBodyContainer: INodeProps,
        subscriptionDetailsData?: IGetSubscriptionDetailsData
    ) => {
        const editCustomerReference = (this.state && this.state.editCustomerReference) || false;
        const cancel = () => {
            this.setState({ editCustomerReference: false });
            this.setState({ customerReferenceState: this.state.subscriptionDetailsData?.CustomerReference || '' });
        };
        const handlePaymentClick = () => {
            window.open(subscriptionDetailsData?.PaymentLinkUrl, '_blank');
        };
        return (
            <>
                <b>{subscriptionDetailsData?.ScheduleNumber}</b>
                <div className='row nos-subscription-details__header-padding'>
                    <div className='col-4'>{resources.subDetailsStatus}</div>
                    <div className='col-md'>
                        {getSubscriptionStatus(resources, subscriptionDetailsData?.Status)}
                        {subscriptionDetailsData?.PaymentFailed && ` : ${resources.subDetailsPaymentFailed}`}
                        {subscriptionDetailsData?.PaymentFailed && (
                            <Button
                                className='nos-subscription-details__pay-button'
                                aria-label={resources.subDetailsPaymentLinkText}
                                onClick={() => handlePaymentClick()}
                                title={resources.subDetailsPaymentLinkText}
                            >
                                {resources.subDetailsPaymentLinkText}
                            </Button>
                        )}
                    </div>
                </div>
                <div className='row nos-subscription-details__header-padding'>
                    <div className='col-4'>{resources.subDetailsStartDate}</div>
                    <div className='col-md'>
                        {subscriptionDetailsData?.StartDate ? formatDate(context, new Date(subscriptionDetailsData.StartDate)) : '-'}
                    </div>
                </div>
                <div className='row nos-subscription-details__header-padding'>
                    <div className='col-4'>{resources.subDetailsNextPaymentDate}</div>
                    <div className='col-md'>
                        {subscriptionDetailsData?.NextPaymentDate
                            ? formatDate(context, new Date(subscriptionDetailsData.NextPaymentDate))
                            : '-'}
                    </div>
                </div>
                {subscriptionDetailsData?.PaymentMethod && (
                    <div className='row nos-subscription-details__header-padding'>
                        <div className='col-4'>{resources.subDetailsPaymentMethod}</div>
                        <div className='col-md'>
                            {subscriptionDetailsData.PaymentMethod}
                            {subscriptionDetailsData.CreditCardNumber && (
                                <>
                                    , {subscriptionDetailsData.CreditCardTypeName} {resources.endingWith}{' '}
                                    {subscriptionDetailsData.CreditCardNumber}
                                </>
                            )}
                        </div>
                    </div>
                )}
                {subscriptionDetailsData && !editCustomerReference && (
                    <div className='row nos-subscription-details__header-padding'>
                        <div className='col-4'>{resources.subDetailsCustomerReference}</div>
                        <div className='col-md'>
                            {subscriptionDetailsData.CustomerReference ? subscriptionDetailsData.CustomerReference : '-'}
                            <Button
                                className='nos-subscription-details__edit-button'
                                aria-label={`${resources.editButtonText} ${resources.subDetailsCustomerReference}`}
                                onClick={() => this.setState({ editCustomerReference: true })}
                                title={resources.editButtonText}
                            >
                                {resources.editButtonText}
                            </Button>
                        </div>
                    </div>
                )}
                {subscriptionDetailsData && editCustomerReference && (
                    <div className='row nos-subscription-details__header-padding'>
                        <div className='col-4'>{resources.subDetailsCustomerReference}</div>
                        <div className='col-md'>
                            <input
                                type='text'
                                aria-label={resources.subDetailsCustomerReference}
                                className={classnames('nos-subscription-details__customer-reference-input')}
                                value={this.state.customerReferenceState}
                                onChange={e => this.setState({ customerReferenceState: e.target.value })}
                                disabled={!editCustomerReference}
                                ref={this.customerReferenceRef}
                            />
                            <Button
                                className={classnames('nos-subscription-details__save-button')}
                                aria-label={resources.saveBtnLabel}
                                onClick={this.changeCustomerReference}
                                title={resources.saveBtnLabel}
                            >
                                {resources.saveBtnLabel}
                            </Button>
                            <Button
                                className={classnames('nos-subscription-details__cancel-button')}
                                aria-label={resources.cancelButtonAriaLabel}
                                onClick={cancel}
                                title={resources.cancelBtnLabel}
                            >
                                {resources.cancelBtnLabel}
                            </Button>
                        </div>
                    </div>
                )}
                {subscriptionDetailsData?.Status === 0 && (
                    <div className='row nos-subscription-details__header-padding'>
                        <div className='col-4'>{resources.subDetailsAction}</div>
                        <div className='col-md'>
                            <Button
                                disabled={this.state.isCanceled}
                                onClick={() => this.openModal()}
                                className='ms-button__secondary-light'
                            >
                                {resources.subDetailsCancelSubscription}
                            </Button>
                            {this.state.isModalOpen && (
                                <Module {...modalContainer}>
                                    <Node {...modalHeaderContainer}>{resources.subDetailsCancelSubscription}</Node>
                                    <Node {...modalBodyContainer}>
                                        <p>{resources.cancelText}</p>
                                    </Node>
                                    <Node {...modalFooterContainer}>
                                        <Button onClick={() => this.closeModal()} className='ms-button__primary-dark'>
                                            {resources.toggleDisableText}
                                        </Button>
                                        <Button
                                            disabled={this.state.isCanceled}
                                            onClick={() => this.cancelSubscription(subscriptionDetailsData)}
                                            className='ms-button__primary-light'
                                        >
                                            {this.state.isCanceled ? (
                                                <span className='nos-subscription-details__loading'>{resources.loadingMessage}</span>
                                            ) : (
                                                resources.toggleEnableText
                                            )}
                                        </Button>
                                    </Node>
                                </Module>
                            )}
                        </div>
                    </div>
                )}
            </>
        );
    };

    /**
     * Initiate close modal.
     */
    private readonly closeModal = (): void => {
        this.setState({
            isModalOpen: false
        });
    };

    /**
     * Initiate open modal.
     */
    private readonly openModal = (): void => {
        this.setState({
            isModalOpen: true
        });
    };

    private cancelSubscription = async (subscriptionDetailsData?: IGetSubscriptionDetailsData) => {
        if (subscriptionDetailsData) {
            const actionInput = new CancelSubscriptionInput(subscriptionDetailsData.LineNum, subscriptionDetailsData.ScheduleNumber);
            this.setState({ isCanceled: true });
            await cancelSubscriptionAction(actionInput, this.props.context.actionContext).then(res => {
                if (res?.response?.value) {
                    void this._initialize();
                }
            });
        }
    };

    private changeCustomerReference = async () => {
        const input = this.customerReferenceRef.current;
        const value = input?.value || '';
        const scheduleNumber = this.state.subscriptionDetailsData?.ScheduleNumber || '';
        const changeCustomerReferenceInput = new ChangeCustomerReferenceInput(scheduleNumber, value);
        await changeCustomerReferenceAction(changeCustomerReferenceInput, this.props.context.actionContext).then(res => {
            if (res?.response?.value) {
                void this._initialize();
            }
        });
    };

    private _renderSubscriptionTotals = (
        resources: INosSubscriptionDetailsResources,
        subscriptionDetailsData?: IGetSubscriptionDetailsData
    ) => {
        return (
            <React.Fragment>
                <h3 className='nos-subscription-details__header-padding-m'>{resources.subDetailsSubscriptionTotals}</h3>
                <table className='table'>
                    <thead>
                        <tr>
                            <th scope='col'>{resources.subDetailsHeaderProduct}</th>
                            <th scope='col'>{resources.subDetailsHeaderQuantity}</th>
                            <th scope='col'></th>
                            <th scope='col'>{resources.subDetailsHeaderTotal}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{subscriptionDetailsData?.SubscriptionLineTotals?.ItemName}</td>
                            <td>{subscriptionDetailsData?.SubscriptionLineTotals?.Quantity}</td>
                            <td>{resources.subDetailsHeaderSubTotal}</td>
                            <td>
                                {formatCurrency(
                                    this.props,
                                    subscriptionDetailsData?.SubscriptionLineTotals?.CurrencyCode,
                                    Number(subscriptionDetailsData?.SubscriptionLineTotals?.SubTotalAmount)
                                )}{' '}
                                / {getInvoiceFrequency(resources, subscriptionDetailsData?.SubscriptionLineTotals?.Frequency)}
                            </td>
                        </tr>
                        <tr>
                            <td> </td>
                            <td> </td>
                            <td>
                                {resources.subDetailsVatHeaderPercent} {subscriptionDetailsData?.SubscriptionLineTotals?.VATPercantage}
                            </td>
                            <td>
                                {formatCurrency(
                                    this.props,
                                    subscriptionDetailsData?.SubscriptionLineTotals?.CurrencyCode,
                                    Number(subscriptionDetailsData?.SubscriptionLineTotals?.VATAmount)
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td> </td>
                            <td> </td>
                            <td>
                                <strong>{resources.subDetailsHeaderTotal}</strong>
                            </td>
                            <td>
                                <strong>
                                    {formatCurrency(
                                        this.props,
                                        subscriptionDetailsData?.SubscriptionLineTotals?.CurrencyCode,
                                        Number(subscriptionDetailsData?.SubscriptionLineTotals?.TotalAmount)
                                    )}{' '}
                                    / {getInvoiceFrequency(resources, subscriptionDetailsData?.SubscriptionLineTotals?.Frequency)}
                                </strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </React.Fragment>
        );
    };

    private _renderBillingAddress = (
        resources: INosSubscriptionDetailsResources,
        subscriptionDetailsData?: IGetSubscriptionDetailsData
    ) => {
        return (
            <React.Fragment>
                <h3 className='nos-subscription-details__header-padding-m'>{resources.subDetailsHeaderBillingAddress}</h3>
                <div>{subscriptionDetailsData?.SubscriptionAddressBillTo?.Name}</div>
                <div dangerouslySetInnerHTML={{ __html: subscriptionDetailsData?.SubscriptionAddressBillTo?.FormattedAddress! }} />
            </React.Fragment>
        );
    };

    private _renderContactInformation = (
        resources: INosSubscriptionDetailsResources,
        subscriptionDetailsData?: IGetSubscriptionDetailsData
    ) => {
        return (
            <React.Fragment>
                <h3 className='nos-subscription-details__header-padding-m'>{resources.subDetailsHeaderContact}</h3>
                <div>
                    {subscriptionDetailsData?.ContactGivenName} {subscriptionDetailsData?.ContactSurname}
                </div>
                <div>{subscriptionDetailsData?.ContactEmailAddress}</div>
            </React.Fragment>
        );
    };

    private getInvoiceDetailPageUrl = (actionContext: Msdyn365.IActionContext, invoiceId: string): string => {
        const subscriptionsUrl = Msdyn365.getUrlSync('invoice-details', actionContext) || '';
        const separator = subscriptionsUrl.includes('?') ? '&' : '?';
        return `${subscriptionsUrl}${separator}invoiceId=${invoiceId}`;
    };

    private _renderRelatedInvoices = (
        resources: INosSubscriptionDetailsResources,
        context: any,
        subscriptionDetailsData?: IGetSubscriptionDetailsData
    ) => {
        return (
            <React.Fragment>
                <h3 className='nos-subscription-details__header-padding-m'>{resources.subDetailsRelatedInvoices}</h3>
                <table className='table'>
                    <thead>
                        <tr>
                            <th scope='col'>{resources.subDetailsHeaderId}</th>
                            <th scope='col'>{resources.subDetailsHeaderDate}</th>
                            <th scope='col'>{resources.subDetailsStatus}</th>
                            <th scope='col'>{resources.subDetailsHeaderTotal}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subscriptionDetailsData?.SubscriptionRelatedInvoices?.map(el => {
                            if (el.InvoiceId && el.InvoiceId !== '') {
                                return (
                                    <tr key={el.InvoiceId}>
                                        <a href={this.getInvoiceDetailPageUrl(context, el.InvoiceId)} aria-label={el.InvoiceId}>
                                            <td>{el.InvoiceId}</td>
                                        </a>
                                        <td>{el.InvoiceDate && formatDate(context, new Date(el.InvoiceDate))}</td>
                                        <td>{getInvoiceStatus(resources, el.InvoicePaymentStatus)}</td>
                                        <td>
                                            <strong>
                                                {el.InvoiceTotalAmount
                                                    ? formatCurrency(this.props, el.InvoiceCurrencyCode, Number(el.InvoiceTotalAmount))
                                                    : '-'}
                                            </strong>
                                        </td>
                                    </tr>
                                );
                            } else {
                                return undefined;
                            }
                        })}
                    </tbody>
                </table>
            </React.Fragment>
        );
    };
}

export default NosSubscriptionDetails;
